import { useState } from "react";
import { Check, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { addPerson, validateNewPersonEmail, type NewPersonProblem } from "@/lib/access";

/**
 * The onboarding control the access screen was missing (AC-3, REQ-PORTAL-019).
 *
 * Modelled on Meteoric's `AddPersonBar`, which is the reference for "easy to
 * use" here. Two things it keeps deliberately:
 *
 * * **A stated reason, never a silent no-op.** A mistyped address that quietly
 *   does nothing is how an onboarding does not happen and nobody finds out
 *   until the person cannot sign in.
 * * **Adding grants nothing.** The person lands with every area at `—`, and the
 *   grant is the next, separate, audited act. Collapsing the two is how someone
 *   ends up with an area nobody chose for them (I-1).
 *
 * Collapsed by default: the grid is a spot-the-anomaly scan (UX W4), and a
 * permanent form at the top of it is a permanent distraction from the scan.
 */
const PROBLEMS: Record<Exclude<NewPersonProblem, "ok">, string> = {
  empty: "Enter a ketosource.co email.",
  "not-ketosource": "Only ketosource.co email addresses can be added.",
  duplicate: "That person is already listed.",
};

export function AddPersonBar({
  existingEmails,
  onAdded,
}: {
  existingEmails: string[];
  onAdded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const problem = validateNewPersonEmail(email, existingEmails);
    if (problem !== "ok") {
      setError(PROBLEMS[problem]);
      return;
    }
    setSaving(true);
    setError(null);
    // The API re-checks the domain — this is the fast, local half of the same
    // rule, not the control. The control has to live where the write happens.
    const message = await addPerson(email, name);
    setSaving(false);
    if (message) {
      setError(message);
      return;
    }
    setEmail("");
    setName("");
    setOpen(false);
    onAdded();
  };

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        Add person
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-ks-grey-4 p-4 md:flex-row md:items-start">
      <div className="grid flex-1 gap-3 md:grid-cols-2">
        <Input
          value={email}
          aria-label="Email"
          placeholder={`name@ketosource.co`}
          onChange={(event) => setEmail(event.currentTarget.value)}
        />
        <Input
          value={name}
          aria-label="Name"
          placeholder="Name"
          onChange={(event) => setName(event.currentTarget.value)}
        />
        {error && (
          <p role="alert" className="text-sm text-ks-ink md:col-span-2">
            {error}
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <Button disabled={saving} onClick={() => void submit()}>
          <Check className="h-4 w-4" aria-hidden="true" />
          {saving ? "Adding" : "Add"}
        </Button>
        <Button variant="secondary" onClick={() => setOpen(false)}>
          <X className="h-4 w-4" aria-hidden="true" />
          Cancel
        </Button>
      </div>
    </div>
  );
}
